import type { CacheBackend } from "@pdfking/storages"
import type { AnyGUID, StringGUID } from "@pdfking/tfunc"
import { Constant, GuidToString, ProcessIterable, ReplayBroadcast, Partial } from "@pdfking/tfunc"

import type { MessageRouter } from "#public"
import type { ReceiverInput } from "#public"
import type { ReceiverOutput } from "#public"

const LockedForward = <T, R>(
  lock_by: (input: ReceiverInput<T>) => Promise<[StringGUID, () => ReceiverOutput<R>]>
): MessageRouter<T, R> => {
  const channels: Map<StringGUID, ReturnType<typeof ReplayBroadcast<R>>> = new Map()

  return async function* (request: ReceiverInput<T>) {
    const [key, next] = await lock_by(request)

    const channel = channels.get(key)

    if (channel) {
      yield* channel.subscribe()
    } else {
      const channel = ReplayBroadcast<R>()
      channels.set(key, channel)

      void ProcessIterable(next(), channel.push, async error => {
        channel.stop(error)
        channels.delete(key)
      })
      yield* channel.subscribe()
    }
  }
}

// what a request does with the cache: USE replays a stored response or stores
// a fresh one, EVICT drops the stored one and stores the re-run, SKIP neither
// reads nor stores
export const enum CacheMode {
  USE = "u",
  EVICT = "e",
  SKIP = "s",
}

const ApplyCache = async function* <R>(
  id: StringGUID,
  stream: () => ReceiverOutput<R>,
  cacheable: (message: R) => boolean,
  mode: CacheMode,
  cache?: CacheBackend<R[]>
): ReceiverOutput<R> {
  if (cache && mode !== CacheMode.SKIP) {
    if (mode === CacheMode.EVICT) await cache.delete(id)

    const existing = mode === CacheMode.EVICT ? undefined : await cache.get(id)

    if (existing) {
      // entries written before `cacheable` existed still hold everything
      yield* existing.filter(cacheable)
    } else {
      const messages: R[] = []
      for await (const message of stream()) {
        if (cacheable(message)) messages.push(message)
        yield message
      }
      await cache.set(id, messages)
    }
  } else {
    yield* stream()
  }
}

export const CachedForward = <T, R>(
  forward: MessageRouter<T, R>,
  resolver: (input: ReceiverInput<T>) => Promise<{
    guid: AnyGUID
    input: ReceiverInput<T>
    mode: CacheMode
  }>,
  // what a response is made of, as opposed to what was said while it was
  // being made: only the former is stored, and only the former is replayed
  cacheable: (message: R) => boolean = Constant(true),
  cache?: CacheBackend<R[]>
): MessageRouter<T, R> =>
  LockedForward(async input => {
    const { guid, input: next, mode } = await resolver(input)
    const key = GuidToString(guid)
    return [key, Partial(ApplyCache, key, Partial(forward, next), cacheable, mode, cache)]
  })
